import global from '../shim/global';
/**
 * A cache of results of feature tests
 */
export const testCache = {};
/**
 * A cache of the un-resolved feature tests
 */
export const testFunctions = {};
/* Grab the staticFeatures if there are available */
const { staticFeatures } = global.DojoHasEnvironment || {};
/* Cleaning up the DojoHasEnviornment */
if ('DojoHasEnvironment' in global) {
    delete global.DojoHasEnvironment;
}
/**
 * Custom type guard to narrow the `staticFeatures` to either a map or a function that
 * returns a map.
 *
 * @param value The value to guard for
 */
function isStaticFeatureFunction(value) {
    return typeof value === 'function';
}
/**
 * The cache of asserted features that were available in the global scope when the
 * module loaded
 */
const staticCache = staticFeatures
    ? isStaticFeatureFunction(staticFeatures)
        ? staticFeatures.apply(global)
        : staticFeatures
    : {}; /* Providing an empty cache, if none was in the environment


/**
* AMD plugin function.
*
* Resolves resourceId into a module id based on possibly-nested tenary expression that branches on has feature test
* value(s).
*
* @param resourceId The id of the module
* @param normalize Resolves a relative module id into an absolute module id
*/
export function normalize(resourceId, normalize) {
    const tokens = resourceId.match(/[\?:]|[^:\?]*/g) || [];
    let i = 0;
    function get(skip) {
        const term = tokens[i++];
        if (term === ':') {
            // empty string module name, resolves to null
            return null;
        }
        else {
            // postfixed with a ? means it is a feature to branch on, the term is the name of the feature
            if (tokens[i++] === '?') {
                if (!skip && has(term)) {
                    // matched the feature, get the first value from the options
                    return get();
                }
                else {
                    // did not match, get the second value, passing over the first
                    get(true);
                    return get(skip);
                }
            }
            // a module
            return term;
        }
    }
    const id = get();
    return id && normalize(id);
}
/**
 * Check if a feature has already been registered
 *
 * @param feature the name of the feature
 */
export function exists(feature) {
    const normalizedFeature = feature.toLowerCase();
    return Boolean(normalizedFeature in staticCache || normalizedFeature in testCache || testFunctions[normalizedFeature]);
}
/**
 * Register a new test for a named feature.
 *
 * @example
 * has.add('dom-addeventlistener', !!document.addEventListener);
 *
 * @example
 * has.add('touch-events', function () {
 *    return 'ontouchstart' in document
 * });
 *
 * @param feature the name of the feature
 * @param value the value reported of the feature, or a function that will be executed once on first test
 * @param overwrite if an existing value should be overwritten. Defaults to false.
 */
export function add(feature, value, overwrite = false) {
    const normalizedFeature = feature.toLowerCase();
    if (exists(normalizedFeature) && !overwrite && !(normalizedFeature in staticCache)) {
        throw new TypeError(`Feature "${feature}" exists and overwrite not true.`);
    }
    if (typeof value === 'function') {
        testFunctions[normalizedFeature] = value;
    }
    else {
        testCache[normalizedFeature] = value;
        delete testFunctions[normalizedFeature];
    }
}
/**
 * Return the current value of a named feature.
 *
 * @param feature The name of the feature to test.
 */
export default function has(feature, strict = false) {
    let result;
    const normalizedFeature = feature.toLowerCase();
    if (normalizedFeature in staticCache) {
        result = staticCache[normalizedFeature];
    }
    else if (testFunctions[normalizedFeature]) {
        result = testCache[normalizedFeature] = testFunctions[normalizedFeature].call(null);
        delete testFunctions[normalizedFeature];
    }
    else if (normalizedFeature in testCache) {
        result = testCache[normalizedFeature];
    }
    else if (strict) {
        throw new TypeError(`Attempt to detect unregistered has feature "${feature}"`);
    }
    return result;
}
/*
 * Out of the box feature tests
 */
add('public-path', undefined);
/* flag for dojo debug, default to false */
add('dojo-debug', false);
/* Detects if the environment is "browser like" */
add('host-browser', true);
/* Detects if the environment is "jsdom" */
add('host-jsdom', true && typeof navigator !== 'undefined' && navigator.userAgent.indexOf('jsdom') !== -1);
/* Detects if the environment appears to be NodeJS */
add('host-node', false);
add('fetch', 'fetch' in global && typeof global.fetch === 'function', true);
add('es6-array', true);
add('es6-array-fill', true);
add('es7-array', true);
add('es2019-array', true);
/* Map */
add('es6-map', true);
add('es6-iterator', true);
/* Math */
add('es6-math', true);
add('es6-math-imul', true);
/* Object */
add('es6-object', true);
add('es2017-object', true);
/* Observable */
add('es-observable', false);
/* Promise */
add('es6-promise', true);
add('es2018-promise-finally', () => true && typeof global.Promise.prototype.finally !== 'undefined', true);
/* Set */
add('es6-set', true);
/* String */
add('es6-string', true);
add('es6-string-raw', true);
add('es2017-string', true);
/* Symbol */
add('es6-symbol', true);
/* WeakMap */
add('es6-weakmap', true);
/* Miscellaneous features */
add('microtasks', true);
add('postmessage', true);
add('raf', true);
add('setimmediate', false);
/* DOM Features */
add('dom-mutationobserver', true);
add('dom-webanimation', () => true && global.Animation !== undefined && global.KeyframeEffect !== undefined, true);
add('abort-controller', () => typeof global.AbortController !== 'undefined');
add('abort-signal', () => typeof global.AbortSignal !== 'undefined');
add('dom-intersection-observer', () => true && global.IntersectionObserver !== undefined, true);
add('dom-resize-observer', () => true && global.ResizeObserver !== undefined, true);
add('dom-pointer-events', () => true && global.onpointerdown !== undefined, true);
add('dom-css-variables', true);
add('dom-inert', () => true && Element.prototype.hasOwnProperty('inert'), true);
add('dom-passive-event', () => {
    let supportsPassive = false;
    if ('host-browser') {
        try {
            const opts = Object.defineProperty({}, 'passive', {
                get() {
                    supportsPassive = true;
                }
            });
            const f = () => { };
            window.addEventListener('testPassive', f, opts);
            window.removeEventListener('testPassive', f, opts);
        }
        catch (e) { }
    }
    return supportsPassive;
}, true);
add('build-elide', false);
add('test', false);
add('global-this', () => typeof global.globalThis !== 'undefined');
//# sourceMappingURL=has.mjs.map